/**
 * Spell preview for the HUD (DESIGN 5.6 "Spell preview", 6.5): what the
 * current queue does in every cast mode, its names, and the magicks the
 * queue is a prefix of. The rules engine is pure, so the result is cached
 * by queue contents and recomputed only when the queue changes.
 */

import type { CastMode, Element, MagickId, SpellSpec } from '@shared/index.js';
import { CAST_MODES, magickPrefix, resolveCast, spellName } from '@shared/index.js';

export interface SpellPreview {
  /** resolveCast(queue, mode) for every mode. */
  preview: Record<CastMode, SpellSpec>;
  /** spellName(preview[mode]). */
  previewName: Record<CastMode, string>;
  /** magickPrefix(queue). */
  magickHint: MagickId[];
}

export class PreviewCache {
  private key: string | null = null;
  private value: SpellPreview | null = null;

  /** The preview for `queue`, reusing the last one when the contents match. */
  for(queue: readonly Element[]): SpellPreview {
    const key = queue.join(',');
    if (this.value && this.key === key) return this.value;
    const preview = {} as Record<CastMode, SpellSpec>;
    const previewName = {} as Record<CastMode, string>;
    for (const mode of CAST_MODES) {
      const spec = resolveCast(queue, mode);
      preview[mode] = spec;
      previewName[mode] = spellName(spec);
    }
    this.key = key;
    this.value = { preview, previewName, magickHint: magickPrefix(queue) };
    return this.value;
  }

  clear(): void {
    this.key = null;
    this.value = null;
  }
}
